// app/LayoutClient.tsx
'use client';

import { usePathname } from "next/navigation";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function LayoutClient({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const { status } = useAuth();
  const isPublicShareRoute = /^\/share\/(ebook|bookshelf)\/[^/]+/.test(
    pathname ?? ""
  );

  useEffect(() => {
    if (!isPublicShareRoute && status === "unauthenticated") {
      router.replace("/api/auth/callback");
    }
  }, [isPublicShareRoute, router, status]);

  if (isPublicShareRoute) {
    return (
      <>
        {children}
        <Toaster position="top-right" richColors />
      </>
    );
  }

  if (status === "loading" || status === "unauthenticated") {
    return <main className="min-h-screen bg-white" aria-busy="true" />;
  }

  if (status === "forbidden" || status === "error") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-white px-6 text-center">
        <div>
          <h1 className="text-xl font-semibold">Unable to load your workspace</h1>
          <p className="mt-2 text-sm text-gray-600">Please try again or contact your administrator.</p>
          <button
            type="button"
            className="mt-4 rounded-md bg-[#CD2834] px-4 py-2 text-sm text-white"
            onClick={() => window.location.reload()}
          >
            Try again
          </button>
        </div>
      </main>
    );
  }
  
  const hideNavbarFooter = [
    /^\/bookshelf\/[^/]+\/customize/,
    /^\/ebook\/[^/]+\/customize/,
  ].some((pathPattern) => pathPattern.test(pathname ?? ""));

  return (
    <>
      {!hideNavbarFooter && <Navbar />}
      {children}
      <Toaster position="top-right" richColors />
      {!hideNavbarFooter && <Footer />}
    </>
  );
}
